import EmptyState from '@/components/empty-state';
import PageHeader from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { app_customers_path } from '@/rails/routes';
import type { AppShell, Customer } from '@/types/platform';
import { Form } from '@inertiajs/react';

interface CustomersIndexProps {
  app: AppShell;
  customers: Customer[];
}

const CustomersIndex = ({ app, customers }: CustomersIndexProps) => {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Customers"
        description="The people and companies you bill. Every app on your account shares this list."
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="min-w-0 lg:col-span-2">
          <CardContent>
            {customers.length === 0 ? (
              <EmptyState
                title="No customers yet"
                description="Add a customer to pick them on a record or bill them on an invoice."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers.map((customer) => (
                    <TableRow key={customer.id}>
                      <TableCell className="font-medium">{customer.name}</TableCell>
                      <TableCell>{customer.email}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card className="min-w-0 self-start">
          <CardHeader>
            <CardTitle>
              <h2>Add a customer</h2>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* A saved customer grows the list, which remounts the form empty. A refused one keeps what was typed. */}
            <Form
              key={customers.length}
              method="post"
              action={app_customers_path(app.id)}
              options={{ preserveScroll: true }}
              className="space-y-4"
            >
              {({ processing }) => (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="customer-name">Name</Label>
                    <Input id="customer-name" name="customer[name]" autoComplete="off" required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customer-email">Email</Label>
                    <Input
                      id="customer-email"
                      name="customer[email]"
                      type="email"
                      autoComplete="off"
                      required
                    />
                    <p className="text-muted-foreground text-xs">Invoices are sent here.</p>
                  </div>
                  <Button type="submit" disabled={processing}>
                    Add customer
                  </Button>
                </>
              )}
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CustomersIndex;
